import { useState, useEffect, useRef, useCallback } from 'react';
import { VoiceSegment, MessageSegment, QueueSegment } from '../types/avatar';
import { getLipsyncManager } from '../lib/lipsync';
import { generateTextToLipsync, estimateDuration, MouthCue } from '../lib/textToLipsync';

interface UseVoiceQueueReturn {
  currentSegment: QueueSegment | null;
  currentIndex: number;
  isPlaying: boolean;
  currentViseme: string | null; // For manual lipsync driving
  nextSegment: () => void;
  previousSegment: () => void;
  playSegment: (index: number) => void;
  stopPlayback: () => void;
  resetQueue: () => void;
}

export const useVoiceQueue = (
  voiceQueue: QueueSegment[] = [],
  onComplete?: () => void,
  onSegmentComplete?: (segment: QueueSegment, index: number) => void
): UseVoiceQueueReturn => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentViseme, setCurrentViseme] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);

  const currentSegment = voiceQueue[currentIndex] || null;
  const queueLength = voiceQueue.length; // Extract for dependency

  const stopPlayback = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentViseme(null);
  }, []);

  const playSegment = useCallback((index: number) => {
    if (index < 0 || index >= voiceQueue.length) {
      return;
    }

    const segment = voiceQueue[index];
    if (!segment) return;

    // Stop current playback
    stopPlayback();

    // Check if it's a VoiceSegment (has audioPath)
    if ('audioPath' in segment && segment.audioPath) {
      try {
        // Create new audio element
        const audio = new Audio(segment.audioPath);
        audioRef.current = audio;

        // Set up audio event listeners
        audio.addEventListener('loadeddata', () => {
          setIsPlaying(true);

          // Connect audio to lipsync manager (following Visualizer.jsx pattern)
          try {
            const lipsyncManager = getLipsyncManager();
            lipsyncManager.connectAudio(audio);

            // Start audio processing loop (following Visualizer.jsx pattern)
            const processAudioLoop = () => {
              try {
                lipsyncManager.processAudio();
              } catch (error) {
                // Audio processing failed - continue loop
              }
              animationFrameRef.current = requestAnimationFrame(processAudioLoop);
            };
            processAudioLoop();
          } catch (error) {
            // Lipsync initialization failed - audio will play without lipsync
          }

          audio.play().catch(console.error);
        });

        audio.addEventListener('ended', () => {
          handleSegmentComplete(segment, index);
        });

        audio.addEventListener('error', (e) => {
          console.error('Audio playback error:', e);
          handleSegmentComplete(segment, index);
        });

        // Load the audio
        audio.load();

      } catch (error) {
        console.error('Error setting up audio playback:', error);
        setIsPlaying(false);
      }
    }
    // Handle MessageSegment (text-to-lipsync)
    else if ('text' in segment) {
      const messageSegment = segment as MessageSegment;
      const duration = messageSegment.duration || estimateDuration(messageSegment.text);

      console.log(`Playing message: "${messageSegment.text}" (${duration}s)`);

      const lipsyncData = generateTextToLipsync(messageSegment.text, duration);
      const mouthCues = lipsyncData.mouthCues;

      setIsPlaying(true);
      startTimeRef.current = Date.now();

      const processMessageLoop = () => {
        const elapsed = (Date.now() - startTimeRef.current) / 1000;

        if (elapsed >= duration) {
          handleSegmentComplete(segment, index);
          return;
        }

        // Find current viseme
        const currentCue = mouthCues.find(cue => elapsed >= cue.start && elapsed < cue.end);
        if (currentCue) {
          setCurrentViseme(currentCue.value);
        } else {
          setCurrentViseme('X'); // Closed mouth or default
        }

        animationFrameRef.current = requestAnimationFrame(processMessageLoop);
      };

      processMessageLoop();
    }
  }, [voiceQueue, stopPlayback]); // Removed handleSegmentComplete from deps to avoid cycle, defined below

  const handleSegmentComplete = (segment: QueueSegment, index: number) => {
    setIsPlaying(false);
    setCurrentViseme(null);

    // Stop audio processing loop
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Call segment complete callback
    onSegmentComplete?.(segment, index);

    // Move to next segment or complete
    if (index < voiceQueue.length - 1) {
      setCurrentIndex(index + 1);
    } else {
      // Queue completed
      setCurrentIndex(voiceQueue.length); // Point past the end
      onComplete?.();
    }
  };

  const nextSegment = useCallback(() => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < voiceQueue.length) {
      setCurrentIndex(nextIndex);
    }
  }, [currentIndex, voiceQueue.length]);

  const previousSegment = useCallback(() => {
    const prevIndex = currentIndex - 1;
    if (prevIndex >= 0) {
      setCurrentIndex(prevIndex);
    }
  }, [currentIndex]);

  const resetQueue = useCallback(() => {
    stopPlayback();
    setCurrentIndex(0);
  }, [stopPlayback]);

  // Auto-play when conditions change
  useEffect(() => {
    if (voiceQueue.length > 0 && currentIndex < voiceQueue.length && !isPlaying) {
      playSegment(currentIndex);
    }
  }, [currentIndex, queueLength, playSegment, isPlaying]); // Note: isPlaying in deps to react to it becoming false

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      stopPlayback();
    };
  }, [stopPlayback]);

  return {
    currentSegment,
    currentIndex,
    isPlaying,
    currentViseme,
    nextSegment,
    previousSegment,
    playSegment,
    stopPlayback,
    resetQueue,
  };
};